import * as React from 'react';
import { SvgIcon } from '@mui/material';
import styles from '@/features/inspection-list/components/filter/inspectionsFilter.module.scss';
import { ReactComponent as CrossIcon } from '@/assets/icons/cross.svg';
import { InspectionFilters } from "@/types/InspectionFilters";
import { ALL_CONTROL_POINTS_ID, ControlPoint } from "@/types/ControlPoint";
import { useTranslation } from 'react-i18next';
import { useNetwork } from '@/hooks/useNetwork';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
type LooseValue = string | Date | null | [string | Date | null, string | Date | null];
import { useState } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { getInspectionFilterDates } from '@/utils/inspection.utils';

export interface InspectionFilterProps extends InspectionFilters {
    productCode: string;
    orderReference: string;
    minDate: string;
    maxDate: string;
    startDate: string;
    endDate: string;
    controlPoints: Array<ControlPoint>;
    onProductCodeChange: (productCode: string) => void;
    onOrderReferenceChange: (orderReference: string) => void;
    onControlPointChange: (controlPoint: string) => void;
    onInspectionStartDateChange: (inspectionStartDate: string) => void;
    onInspectionEndDateChange: (inspectionEndDate: string) => void;
}

export const InspectionsFilter: React.FC<InspectionFilterProps> = (inspectionsFilter) => {

    const { t: translate } = useTranslation();

    const [isDoubleView, setIsDoubleView] = useState(true);
    const config = useSelector((state: RootState) => state.appConfig);

    const isOnline = useNetwork();
    const handleProductCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        inspectionsFilter.onProductCodeChange(e.target.value);
    };
    const handleOrderReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        inspectionsFilter.onOrderReferenceChange(e.target.value);
    };

    const handleInspectionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        inspectionsFilter.onControlPointChange(e.target.value);
    };
    const value: LooseValue = [
        inspectionsFilter.startDate ? new Date(inspectionsFilter.startDate) : null,
        inspectionsFilter.endDate ? new Date(inspectionsFilter.endDate) : null
    ];
    const handleDateChange = (range: any) => {
        if (range) {
            const startDate = range[0].toISOString();
            const endDate = range[1].toISOString();
            inspectionsFilter.onInspectionStartDateChange(startDate);
            inspectionsFilter.onInspectionEndDateChange(endDate);
        }
    };
    const resetInspectionFilters = () => {
        inspectionsFilter.onProductCodeChange('');
        inspectionsFilter.onOrderReferenceChange('');
        if (inspectionsFilter.controlPoints?.length > 0) {
            inspectionsFilter.onControlPointChange(inspectionsFilter.controlPoints[0].id);
        } else {
            inspectionsFilter.onControlPointChange('');
        }
        const datesSettings = getInspectionFilterDates(config);
        inspectionsFilter.onInspectionStartDateChange(datesSettings.start);
        inspectionsFilter.onInspectionEndDateChange(datesSettings.end);
        localStorage.removeItem('inspectionProductCode');
        localStorage.removeItem('inspectionOrderReference');
    }
    const showProductCodeFilter = config.filters?.some(filter => filter === 'productCode');
    const showExternalPOFilter = config.filters?.some(filter => filter === 'externalPO');
    const showInternalOrderRef = config[APP_CONFIG_KEY.showInternalOrderRef];
    // Determine placeholder: if both external PO filter and internal order ref are enabled, search both
    const orderRefPlaceholder = showExternalPOFilter || showInternalOrderRef 
        ? 'search_order_reference' 
        : 'search_external_po';
    useEffect(() => {
        const handleResize = () => {
            setIsDoubleView(window.innerWidth > 600); // threshold for switching view
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    function getSearchFilters(): React.ReactNode | Iterable<React.ReactNode> {
        return <div>{showProductCodeFilter && <div className={styles.searchInputWrapper}>
            <input
                type="text"
                placeholder={translate('search_product_code')}
                className={`${styles.searchInput} ${styles.filterBackground}`}
                value={inspectionsFilter.productCode}
                onChange={handleProductCodeChange}
            />
            {inspectionsFilter.productCode && (
                <SvgIcon
                    component={CrossIcon}
                    className={styles.clearIcon}
                    onClick={() => {
                        inspectionsFilter.onProductCodeChange('');
                        localStorage.removeItem('inspectionProductCode');
                    }}
                />
            )}
        </div>
        }{showExternalPOFilter && <div className={styles.searchInputWrapper}>
            <input
                type="text"
                placeholder={translate(orderRefPlaceholder)}
                className={`${styles.searchInput} ${styles.filterBackground}`}
                value={inspectionsFilter.orderReference}
                onChange={handleOrderReferenceChange}
            />
            {inspectionsFilter.orderReference && (
                <SvgIcon
                    component={CrossIcon}
                    className={styles.clearIcon}
                    onClick={() => {
                        inspectionsFilter.onOrderReferenceChange('');
                        localStorage.removeItem('inspectionOrderReference');
                    }}
                />
            )}
        </div>}
        </div>

    }

    return (
        <section className={styles.filterContainer}  >
            <div className={styles.filter}>
                {getSearchFilters()}
                {inspectionsFilter.controlPoints?.length >= 1 && <select className={`${styles.inspectionType} ${styles.filterBackground}`}
                    disabled={!isOnline}
                    onChange={handleInspectionTypeChange}
                    value={inspectionsFilter.controlPoint} >
                    <option value={ALL_CONTROL_POINTS_ID}>
                        {translate('All')}
                    </option>
                    {inspectionsFilter.controlPoints?.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.name}
                        </option>
                    ))}
                </select>}
                <div className={styles.dateRangeContainer}>
                    <DateRangePicker
                        className={styles.filterBackground}
                        onChange={handleDateChange}
                        showLeadingZeros={true}
                        value={value}
                        minDate={new Date(inspectionsFilter.minDate)}
                        maxDate={new Date(inspectionsFilter.maxDate)}
                        calendarProps={{ showDoubleView: isDoubleView }}
                    />
                    <button className={styles.filterButton} onClick={resetInspectionFilters}>
                        {translate('reset')}
                    </button>
                </div>
            </div>
        </section>
    )
}